import { NavLink, useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

interface NavItem {
  segment: string;
  label: string;
  nowrap?: true;
}

/**
 * Sub-tab segments under `/clients/:id`. Order + segment names match the
 * legacy `wf-adminconsole/components/clients/ClientNavigation.tsx` exactly
 * so muscle memory carries across.
 *
 * Only Settings + (eventually) Licenses have real content today; the
 * other tabs render placeholder pages until their data endpoints land.
 */
const NAV_ITEMS: readonly NavItem[] = [
  { segment: 'licenses', label: 'Licenses' },
  { segment: 'fusion-connections', label: 'Fusion Connections', nowrap: true },
  { segment: 'workfront-connections', label: 'Workfront Connections', nowrap: true },
  { segment: 'oauth-apps', label: 'OAuth Apps', nowrap: true },
  { segment: 'github-opts', label: 'GitHub Options' },
  { segment: 'admins', label: 'Client admins', nowrap: true },
  { segment: 'system-logs', label: 'System logs', nowrap: true },
  { segment: 'settings', label: 'Settings' },
];

const TAB_BASE = 'flex items-center justify-center px-3 py-2 leading-tight';
const TAB_ACTIVE = 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-200';
const TAB_INACTIVE =
  'bg-gray-300 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300';

function tabEdgeClasses(index: number, total: number): string {
  if (index === 0) {
    return 'ms-0 rounded-s-lg border border-e-0 border-gray-400 dark:border-gray-600';
  }
  if (index === total - 1) {
    return 'rounded-e-lg border border-gray-400 dark:border-gray-600';
  }
  return 'border border-gray-400 dark:border-gray-600';
}

export function ClientNavigation(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const total = NAV_ITEMS.length;

  return (
    <nav aria-label="Client sections">
      <ul className="flex h-10 items-center -space-x-px text-sm">
        {NAV_ITEMS.map((item, index) => (
          <li key={item.segment}>
            <NavLink
              to={`/clients/${id ?? ''}/${item.segment}`}
              className={({ isActive }) =>
                twMerge(
                  TAB_BASE,
                  tabEdgeClasses(index, total),
                  isActive ? TAB_ACTIVE : TAB_INACTIVE,
                )
              }
            >
              <span className={item.nowrap ? 'whitespace-nowrap' : undefined}>{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
